import { Box, Flex, Progress, Text } from '@chakra-ui/react';
import {
  faHourglassHalf,
  faCircleCheck,
  faTriangleExclamation,
  faCircleExclamation,
  faWarning
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Table } from '@morressier/alembic';
import { IMetadataExtraction } from '@publishing-engine/domain/models';
import { AuthorDetails } from '@publishing-engine/submission-service/src/endpoints/api.response-types';

export type MetadataExtractionPhase =
  | 'started'
  | 'failed'
  | 'completed'
  | 'oneFailed';

interface Props {
  phase: MetadataExtractionPhase;
  manualOverride: boolean;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  onRetry: () => void;
  metadataExtraction: IMetadataExtraction | undefined;
  extractionProgress: number;
  titlePageExtractionProgress: number;
  titlePageExtractionPhase: MetadataExtractionPhase;
  titlePageFile: boolean;
  contentMismatch: boolean;
  missingInformation: boolean;
  TitlePageData: AuthorDetails[];
  MenuscriptData: AuthorDetails[];
  keywordSuggestionEnabled: boolean;
  keywordSuggestionPhase: MetadataExtractionPhase;
  keywordSuggestionProgress: number;
}

const MetadataExtraction: React.FC<Props> = ({
  phase,
  manualOverride,
  metadataExtraction,
  extractionProgress,
  titlePageExtractionProgress,
  titlePageExtractionPhase,
  titlePageFile,
  contentMismatch,
  missingInformation,
  TitlePageData,
  MenuscriptData,
  keywordSuggestionEnabled,
  keywordSuggestionPhase,
  keywordSuggestionProgress
}) => {
  const getCombinedStatus = (): MetadataExtractionPhase => {
    if (titlePageFile) {
      if (phase === 'failed' && titlePageExtractionPhase === 'failed')
        return 'failed';
      if (phase === 'failed' || titlePageExtractionPhase === 'failed')
        return 'oneFailed';
      if (phase === 'completed' && titlePageExtractionPhase === 'completed')
        return 'completed';
      return 'started';
    } else {
      return phase;
    }
  };
  const getExtractionStatusConfig = () => {
    const combinedStatus = getCombinedStatus();
    switch (combinedStatus) {
      case 'started': {
        return {
          message: manualOverride
            ? metadataExtraction?.updatedDescription
            : metadataExtraction?.description,
          heading: metadataExtraction?.title,
          icon: (
            <Flex
              flexDirection="column"
              padding={{ base: 5, md: 6 }}
              borderRadius="8px"
              backgroundColor="neutralSecondaryFill"
            >
              <FontAwesomeIcon fontSize="30px" icon={faHourglassHalf} />
            </Flex>
          )
        };
      }
      case 'failed': {
        return {
          message: metadataExtraction?.failureStatusDescription,
          heading: metadataExtraction?.failureStatusTitle,
          icon: (
            <Flex
              flexDirection="column"
              padding={{ base: 5, md: 8 }}
              borderRadius="8px"
              backgroundColor="errorPrimaryFill"
              color="actionPrimaryCautionBorderHover"
            >
              <FontAwesomeIcon fontSize="40px" icon={faTriangleExclamation} />
            </Flex>
          )
        };
      }
      case 'oneFailed': {
        return {
          heading:
            phase === 'failed'
              ? 'Manuscript Extraction Failed'
              : 'Title Page Extraction Failed',
          message: `We could not extract submission and author details from ${
            phase === 'failed' ? 'manuscript' : 'title page '
          } file. You may retry the extraction, or proceed and add your submission information manually.`,
          icon: (
            <Flex
              flexDirection="column"
              padding={{ base: 5, md: 8 }}
              borderRadius="8px"
              backgroundColor="attentionSecondaryFill"
              color="attentionPrimaryFill"
            >
              <FontAwesomeIcon fontSize="40px" icon={faCircleExclamation} />
            </Flex>
          )
        };
      }

      case 'completed': {
        return {
          message: metadataExtraction?.successStatusDescription,
          icon: (
            <Flex
              flexDirection="column"
              padding={{ base: 5, md: 8 }}
              borderRadius="8px"
              backgroundColor="successSecondaryFill"
              color="successPrimaryContentAccent"
            >
              <FontAwesomeIcon fontSize="40px" icon={faCircleCheck} />
            </Flex>
          ),
          heading: metadataExtraction?.successStatusTitle
        };
      }
    }
  };

  const getContentConfig = () => {
    if (contentMismatch)
      return {
        message:
          'The following author details were found embedded within the main manuscript file, which is usually redacted for peer review. These details should only be present in the separate Title Page file:',
        heading: 'Author Details Found in Manuscript File'
      };
    if (missingInformation)
      return {
        message:
          'The system found some missing information in your uploaded files. You can proceed to the next step to manually enter the missing data or you can go back to the previous step and upload an updated version of your files.',
        heading: 'Missing Information Detected'
      };
    return {
      message: '',
      heading: ''
    };
  };

  const getContentConfigIcon = () => {
    return (
      <Flex
        flexDirection="column"
        padding={{ base: 5, md: 8 }}
        borderRadius="8px"
        backgroundColor={
          contentMismatch ? 'errorPrimaryFill' : 'attentionSecondaryFill'
        }
        color={
          contentMismatch
            ? 'actionSecondaryCautionContent'
            : 'attentionPrimaryFill'
        }
      >
        <FontAwesomeIcon
          fontSize="40px"
          icon={contentMismatch ? faWarning : faCircleExclamation}
        />
      </Flex>
    );
  };

  const ContentTableHeader = () => {
    if (contentMismatch)
      return (
        <Table.HeaderRow gap={4} data-testId="table-header">
          <Table.HeaderCell flex={1}>Name</Table.HeaderCell>
          <Table.HeaderCell flex={1}>Affiliation</Table.HeaderCell>
          <Table.HeaderCell flex={1}>Email</Table.HeaderCell>
        </Table.HeaderRow>
      );
    return null;
  };

  const ContentTableRow = (data: AuthorDetails) => {
    return (
      <Table.Row
        spacing="md"
        gap={4}
        _last={{ borderBottom: 'none' }}
        alignItems={{ base: 'flex-start', md: 'baseline' }}
        flexDirection={{ base: 'column', md: 'row' }}
      >
        <Table.RowCell flex={1}>
          <Text textStyle="headingItem">{data?.author_name || 'N/A'}</Text>
          {data?.orcid && (
            <Flex alignItems="center" flexDirection="row" gap={2} flexWrap="wrap">
              <Text textStyle="textContext">{data?.orcid}</Text>
            </Flex>
          )}
        </Table.RowCell>
        <>
          <Table.RowCell flex={1}>
            <Text>{data?.institution?.length ? data?.institution : 'N/A'}</Text>
          </Table.RowCell>
          <Table.RowCell flex={1}>{data?.email || 'N/A'}</Table.RowCell>
        </>
      </Table.Row>
    );
  };

  if (contentMismatch) {
    return (
      <Flex flexDirection="column" justifyContent="space-between">
        <Flex flexDirection="column" gap={4}>
          {getContentConfigIcon()}
          <Flex
            flexDirection="column"
            gap={2}
            alignItems="center"
            alignSelf="stretch"
          >
            <Text textStyle="headingTitle">{getContentConfig().heading}</Text>
            <Text textStyle="textBody" textAlign="center">
              {getContentConfig().message}
            </Text>
          </Flex>
        </Flex>
        <Text textStyle="headingItem" mt={6}>
          Author Details ({MenuscriptData?.length || 0})
        </Text>
        <Flex
          bg="surfacePrimaryFill"
          flexDirection="column"
          padding={4}
          gap={4}
          mt={4}
        >
          <Box overflowX="auto" width="100%">
            <Box minWidth={{ base: '640px', md: '100%' }}>
              <Table>
                {ContentTableHeader()}
                <Table.Body
                  maxH={MenuscriptData.length > 6 ? 500 : 'auto'}
                  overflowY={'auto'}
                >
                  {MenuscriptData?.length > 0 &&
                    MenuscriptData?.map(data => ContentTableRow(data))}
                </Table.Body>
              </Table>
            </Box>
          </Box>
        </Flex>
      </Flex>
    );
  } else if (missingInformation) {
    return (
      <Flex flexDirection="column" justifyContent="space-between">
        <Flex flexDirection="column" gap={4}>
          {getContentConfigIcon()}
          <Flex
            flexDirection="column"
            gap={2}
            alignItems="center"
            alignSelf="stretch"
          >
            <Text textStyle="headingTitle">{getContentConfig().heading}</Text>
            <Text textStyle="textContext" textAlign="center">
              {getContentConfig().message}
            </Text>
          </Flex>
        </Flex>
        <Text textStyle="headingItem" mt={6}>
          Missing Content (1)
        </Text>
        {TitlePageData.length === 0 && (
          <Flex
            bg="surfacePrimaryFill"
            flexDirection="column"
            padding={4}
            gap={4}
            mt={4}
          >
            <Text textStyle="headingItem">Authors</Text>
          </Flex>
        )}
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" justifyContent="space-between">
      <Flex flexDirection="column" gap={4}>
        {getExtractionStatusConfig().icon}
        <Flex
          flexDirection="column"
          gap={2}
          alignItems="center"
          alignSelf="stretch"
        >
          <Text textStyle="headingTitle">
            {getExtractionStatusConfig().heading}
          </Text>
          <Text textStyle="textContext" textAlign="center">
            {getExtractionStatusConfig().message}
          </Text>
        </Flex>
      </Flex>
      {titlePageFile && (
        <Flex
          bg="surfacePrimaryFill"
          flexDirection="column"
          padding={4}
          gap={4}
          mt={4}
        >
          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            width={'100%'}
            gap={1}
          >
            <Text textStyle="headingItem">Title Page</Text>
            <Text textAlign={{ base: 'left', md: 'right' }}>{`Extract${
              titlePageExtractionPhase === 'started' ? 'ing' : 'ion'
            } ${
              titlePageExtractionPhase === 'started'
                ? 'file ...'
                : titlePageExtractionPhase === 'completed'
                ? 'complete'
                : 'Failed'
            }`}</Text>
          </Flex>
          <Progress
            value={titlePageExtractionProgress}
            size="sm"
            colorScheme={
              titlePageExtractionPhase === 'started'
                ? 'blue'
                : titlePageExtractionPhase === 'completed'
                ? 'green'
                : 'red'
            }
          />
        </Flex>
      )}
      <Flex
        bg="surfacePrimaryFill"
        flexDirection="column"
        padding={4}
        gap={4}
        mt={4}
      >
        <Flex
          flexDirection={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          width={'100%'}
          gap={1}
        >
          <Text textStyle="headingItem">Manuscript</Text>
          <Text textAlign={{ base: 'left', md: 'right' }}>{`Extract${phase === 'started' ? 'ing' : 'ion'} ${
            phase === 'started'
              ? 'file ...'
              : phase === 'completed'
              ? 'complete'
              : 'Failed'
          }`}</Text>
        </Flex>
        <Progress
          value={extractionProgress}
          colorScheme={
            phase === 'started'
              ? 'blue'
              : phase === 'completed'
              ? 'green'
              : 'red'
          }
          size="sm"
        />
      </Flex>
      {keywordSuggestionEnabled && (
        <Flex
          bg="surfacePrimaryFill"
          flexDirection="column"
          padding={4}
          gap={4}
          mt={4}
        >
          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            width="100%"
            gap={1}
          >
            <Text textStyle="headingItem">Keyword Suggestion</Text>
            <Text textAlign={{ base: 'left', md: 'right' }}>{`Suggest${
              keywordSuggestionPhase === 'started' ? 'ing' : 'ion'
            } ${
              keywordSuggestionPhase === 'started'
                ? 'keywords ...'
                : keywordSuggestionPhase === 'completed'
                ? 'complete'
                : 'Failed'
            }`}</Text>
          </Flex>
          <Progress
            value={keywordSuggestionProgress}
            size="sm"
            colorScheme={
              keywordSuggestionPhase === 'started'
                ? 'blue'
                : keywordSuggestionPhase === 'completed'
                ? 'green'
                : 'red'
            }
          />
        </Flex>
      )}
    </Flex>
  );
};

export default MetadataExtraction;
